import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { login as loginRequest } from "../api/authApi";
import { CMS_ADMIN_TOKEN, CMS_ADMIN_USER } from "../utils/storageKeys";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(CMS_ADMIN_USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(CMS_ADMIN_TOKEN));
  const [user, setUser] = useState(readStoredUser);

  const isAuthenticated = Boolean(token) && user?.role === "Admin";

  const login = useCallback(async (username, password) => {
    const data = await loginRequest(username, password);

    if (!data?.token) {
      throw new Error("Login succeeded but no token was returned.");
    }

    if (data.role !== "Admin") {
      throw new Error("You do not have admin access.");
    }

    localStorage.setItem(CMS_ADMIN_TOKEN, data.token);
    localStorage.setItem(
      CMS_ADMIN_USER,
      JSON.stringify({
        username: data.username,
        role: data.role,
        expiresInMinutes: data.expiresInMinutes,
      })
    );

    setToken(data.token);
    setUser({
      username: data.username,
      role: data.role,
      expiresInMinutes: data.expiresInMinutes,
    });

    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(CMS_ADMIN_TOKEN);
    localStorage.removeItem(CMS_ADMIN_USER);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated,
      login,
      logout,
    }),
    [token, user, isAuthenticated, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
